import React, { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import { ElementState, Preview } from "@creatomate/preview";
import { Thumbnail } from "chq";

import { useDeepCompareEffect } from "app/hooks";
import { VIDEO_TYPE } from "../utils/constants";

interface PartialSource {
  source: { name: string; source: string };
}

const PORTRAIT_MIN_THUMB_WIDTH = 65;
const MIN_THUMB_WIDTH = 80;
const MAX_THUMB_WIDTH = 150;
const THUMB_HEIGHT = 75;
const PORTRAIT_THUMBS_PER_SECOND = 2;

interface TimelineContextType {
  MIN_THUMB_WIDTH: number;
  MAX_THUMB_WIDTH: number;
  PORTRAIT_THUMBS_PER_SECOND: number;
  portrait: boolean;
  getThumbnails: (element: ElementState) => Thumbnail[];
  cleanUp: () => void;
}

interface ThumbnailProviderProps {
  children: ReactNode;
  preview: Preview;
}

const captureFrame = (
  video: HTMLVideoElement,
  currentTime: number,
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D | null
) => new Promise<string>(
  // eslint-disable-next-line no-async-promise-executor
  async resolve => {
    if (ctx === null) {
      resolve("");
      return;
    }

    // eslint-disable-next-line no-param-reassign
    video.currentTime = currentTime;
    await video.play();
    // eslint-disable-next-line require-atomic-updates
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    resolve(canvas.toDataURL("image/png"));
  });

const checkFalseUrl = (src: string): string | undefined => {
  if (/^https:\/\/(eu-)?platform\.culturehq\.com/.test(src) || /com\/\d+$/.test(src)) { return; }

  // eslint-disable-next-line consistent-return
  return src;
};

const getNumberOfThumbsPerSecond = (
  duration: number, fixedWidth: boolean, height: number, width: number
): number => {
  if (fixedWidth) {
    if (height > width) {
      return 1 / PORTRAIT_THUMBS_PER_SECOND;
    }

    return 1;
  }

  const step = 10; // Define the step for each range
  const range = Math.floor(duration / step) * step;

  // a formula to get how many tumbs per second we should get
  return (range / 20) + 0.5;
};

const getSourceUrl = (
  element: ElementState | PartialSource
): string | undefined => {
  let src;
  if (element.source.name?.startsWith("media-https")) {
    src = element.source.name.replace("media-", "");
  } else if (element.source.name?.includes("-fileUrl-")) {
    // eslint-disable-next-line prefer-destructuring
    src = element.source.name.split("-fileUrl-")[1];
  } else {
    src = element.source.source;
  }
  return checkFalseUrl(src);
};

const TimelineContext = createContext<TimelineContextType>({
  MIN_THUMB_WIDTH: 0,
  MAX_THUMB_WIDTH: 0,
  PORTRAIT_THUMBS_PER_SECOND: 0,
  portrait: false,
  getThumbnails: (_element: ElementState) => [],
  cleanUp: () => {}
});

export const useVideo = () => useContext(TimelineContext);

export const ThumbnailProvider: React.FC<ThumbnailProviderProps> = ({ children, preview }) => {
  const isMounted = useRef<boolean | undefined>();

  const [thumbnailSegmentation, setThumbnailSegmentation] = useState<Record<string, {
    timestamp: number;
    thumbnail: string;
  }[]>>();
  const [portrait, setPortrait] = useState(false);
  const [videoElements, setVideoElements] = useState<(string | undefined)[]>([]);

  const videos = useMemo(
    () => (preview.state?.elements?.filter(
      element => element.source.type === VIDEO_TYPE
    ) || []).map(element => ({
      source: { name: element.source.name, source: element.source.source }
    })),
    [preview.state]
  );

  useDeepCompareEffect(
    () => {
      setVideoElements(videos.map(video => getSourceUrl(video)));
    }, [videos]
  );

  useEffect(() => {
    if (!videoElements.length) return;

    for (let index = 0; index < videoElements.length; index += 1) {
      const source = videoElements[index];

      if (source) {
        if (!thumbnailSegmentation?.[source]) {
          if (isMounted.current) setThumbnailSegmentation(prev => ({ ...prev, [source]: [] }));
          const video = document.createElement("video");
          video.setAttribute("controlsList", "nodownload");
          video.setAttribute("crossOrigin", "anonymous");
          video.setAttribute("disablePictureInPicture", "true");
          video.volume = 0;

          const handleLoadedMetadata = async () => {
            if (video.videoHeight > video.videoWidth) {
              setPortrait(true);
            }
            const frameWidth = (video.videoWidth * THUMB_HEIGHT) / video.videoHeight;
            await processVideo(video, source, frameWidth);
            video.removeEventListener("loadeddata", handleLoadedMetadata);
          };

          video.addEventListener("loadeddata", handleLoadedMetadata);
          video.setAttribute("src", source.startsWith("blob:")
            ? source
            : `${source}?not-from-cache-please-${Date.now()}`);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoElements]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const processVideo = async (video: HTMLVideoElement, source: string, frameWidth: number) => {
    const canvas = document.createElement("canvas");
    canvas.width = frameWidth;
    canvas.height = THUMB_HEIGHT;
    const ctx = canvas.getContext("2d");
    const thumbsPerSecond = getNumberOfThumbsPerSecond(
      video.duration, true, video.videoHeight, video.videoWidth
    );
    // eslint-disable-next-line no-plusplus
    for (let timestamp = 0; timestamp < video.duration; timestamp += thumbsPerSecond) {
      // eslint-disable-next-line no-await-in-loop
      const thumbnail = await captureFrame(video, timestamp, canvas, ctx);
      if (!isMounted.current) break;
      else {
        setThumbnailSegmentation(prev => {
          const prevState = prev || {};
          return {
            ...prevState,
            [source]: [...prevState[source] || [], { timestamp, thumbnail }]
          };
        });
      }
    }
  };

  const value = useMemo(() => ({
    MIN_THUMB_WIDTH: portrait
      ? PORTRAIT_MIN_THUMB_WIDTH / PORTRAIT_THUMBS_PER_SECOND : MIN_THUMB_WIDTH,
    MAX_THUMB_WIDTH: portrait ? MAX_THUMB_WIDTH / PORTRAIT_THUMBS_PER_SECOND : MAX_THUMB_WIDTH,
    PORTRAIT_THUMBS_PER_SECOND,
    portrait,
    getThumbnails: (element: ElementState) => {
      if (!thumbnailSegmentation) return [];
      const source = getSourceUrl(element);
      if (!source) return [];
      const thumbnails = thumbnailSegmentation[source];
      if (!thumbnails || !Array.isArray(thumbnails)) return [];

      return thumbnails;
    },
    cleanUp: () => setThumbnailSegmentation(undefined)
  }), [portrait, thumbnailSegmentation]);

  return (
    <TimelineContext.Provider value={value}>
      {children}
    </TimelineContext.Provider>
  );
};
